import { appConfig } from "@webcore/config/appConfig";

/**
 * Installs a window message listener that answers `dashboard-widget/capture-request` messages with
 * a PNG snapshot of the widget's content element (the `[data-mfe-snapshot]` node, falling back to
 * the document body). The main app's PDF export uses it to capture custom
 * widget output that renders inside this (cross-origin) MFE iframe, which the parent cannot
 * rasterize directly. Only requests from the main app origin are honored, and the snapshot is sent
 * back to that origin. Returns a cleanup function suitable for returning from useEffect.
 */
export function installWidgetSnapshotResponder(): () => void {
  const appOrigin = new URL(appConfig.appBaseUrl).origin;

  const handler = async (event: MessageEvent) => {
    if (event.origin !== appOrigin) return;
    const data = event.data;
    if (!data || typeof data !== "object") return;
    if (data.type !== "dashboard-widget/capture-request") return;

    const source = event.source as Window | null;
    if (!source) return;

    const nonce = typeof data.nonce === "string" ? data.nonce : "";

    const html2canvas = (await import("html2canvas")).default;
    // Capture only the widget's content element, not the whole body. Capturing document.body
    // pulls in the full-height wrapper and any sibling chrome, which html2canvas collapses into a
    // band of overlapping text above the real content.
    const target = (document.querySelector("[data-mfe-snapshot]") as HTMLElement | null) ?? document.body;
    const canvas = await html2canvas(target, {
      useCORS: true,
      scale: 2,
      backgroundColor: null,
      logging: false
    });

    source.postMessage({
      type: "dashboard-widget/capture-response",
      nonce,
      imgData: canvas.toDataURL("image/png"),
      width: canvas.width,
      height: canvas.height
    }, event.origin);
  };

  window.addEventListener("message", handler);
  return () => window.removeEventListener("message", handler);
}
